"use client";

import * as stylex from "@stylexjs/stylex";
import {
  Alert,
  AsyncRegion,
  Button,
  Inline,
  Spinner,
  Text,
} from "@softmax/design-system";
import {
  breakpoints,
  controlSizes,
} from "@softmax/design-system/tokens.stylex";
import { useCallback, useRef, useState } from "react";
import type { ChartState } from "./resource-state";

const styles = stylex.create({
  statusLine: {
    display: "flex",
    alignItems: "center",
    minHeight: {
      default: controlSizes.compact,
      [breakpoints.coarse]: controlSizes.touch,
    },
    minWidth: 0,
    flex: 1,
  },
});

/**
 * Show read progress and recovery beside content that the application keeps mounted. Use AsyncRegion for a
 * missing view's content-shaped placeholder. Applications supply readiness and own requests and retained results.
 * Status: supported.
 */
export function ResourceFeedback({
  state,
  subject,
  onRefresh,
  refreshable = false,
}: Readonly<{
  state: ChartState<object> | Readonly<{ status: "idle" }>;
  subject: string;
  onRefresh(): void;
  refreshable?: boolean;
}>) {
  const [retried, setRetried] = useState(false);
  const region = useRef<HTMLElement>(null);
  const retryRef = useCallback((button: HTMLButtonElement | null) => {
    if (button === null) return;

    return () => {
      if (button.ownerDocument.activeElement === button)
        region.current?.focus();
    };
  }, []);
  const refresh = state.status === "ready" ? state.refresh : undefined;
  const pending =
    state.status === "loading" || refresh?.status === "refreshing";
  let message: string | undefined;

  if (state.status === "error") {
    message = state.message;
  } else if (refresh?.status === "error") {
    message = `${refresh.message} Showing the last successful result.`;
  }

  const action =
    message || (!refreshable && retried)
      ? `Retry ${subject}`
      : state.status === "idle"
        ? `Load ${subject}`
        : `Refresh ${subject}`;
  const progress =
    state.status === "loading"
      ? `Loading ${subject}…`
      : `Refreshing ${subject}…`;
  const showAction =
    refreshable ||
    message !== undefined ||
    state.status === "idle" ||
    (pending && retried);
  return (
    <section ref={region} tabIndex={-1} aria-label={`${subject} status`}>
      <Inline justify="between">
        <div {...stylex.props(styles.statusLine)}>
          {message !== undefined ? (
            <Alert tone="danger" announcement="polite">
              {message}
            </Alert>
          ) : (
            <AsyncRegion
              pending={pending}
              label={progress}
              placeholder={
                <Inline gap={8}>
                  <Spinner />
                  <Text size="small" tone="muted">
                    {progress}
                  </Text>
                </Inline>
              }
            />
          )}
        </div>
        {showAction && (
          <Button
            ref={retryRef}
            size="sm"
            aria-disabled={pending}
            aria-busy={pending}
            onClick={() => {
              if (!pending) {
                setRetried(true);
                onRefresh();
              }
            }}
          >
            {action}
          </Button>
        )}
      </Inline>
    </section>
  );
}
